import React, {
  useRef,
  useEffect,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import { useTheme } from 'emotion-theming';
import moment from 'moment-timezone';
import { DragDropContext } from 'react-beautiful-dnd';
import IcomoonReact from 'icomoon-react';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import IconSideBar from './IconSideBar';
import AnimatedSideBarPrimary from './AnimatedSideBarPrimary';
import CalendarPanel from './CalendarPanel';
import SubHeader from './SubHeader';
import Text from '../../../../shared/components/Text/Text';
import AnimatedNumber from './AnimatedNumber';
import useDragManager from '../../hooks/useDragManager';
import useMonthViewDragManager from '../../hooks/useMonthViewDragManager';
import ProgramLibrarySubHeader from './ProgramLibrarySubHeader';
import ProgramLibraryFooter from './ProgramLibraryFooter';
import WeekViewDayToggleFooter from './WeekViewDayToggleFooter';
import { hasKeys } from '../../../../shared/utils/isEmptyObject';

import {
  setCalendarContainerScrollPositionTop,
  setDroppableContainerCurrentlyHoveredByDraggedItem,
  setActiveExerciseId,
  setIsTvMode,
} from '../../ducks/calendarActions';
import { getMonthViewWindowLabel } from '../../utils/monthViewWindow';
import getProgramDayTotals from '../../utils/programDayTotals';

const CalendarContentAndStats = styled('div')`
  display: flex;
  flex: 1;
  flex-direction: column;
  ${(props) => props.viewType !== 'weekView' && props.viewType !== 'monthView' && 'overflow-x: hidden;'}
  ${(props) => props.viewType === 'weekView' && !props.isTvMode && 'padding-left: 48px;'}
  ${(props) => props.viewType === 'monthView' && 'padding: 0; min-width: 0;'}
`;

const StatsBar = styled('div')`
  background: white;
  min-height: 40px;
  border-top: 1px solid #EEEEEE;
  width: 100%;
  justify-content: center;
  align-items: center;
  display: flex;
`;

const MonthYearWeekSubHeaderWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  margin: ${(props) => (!props.isTvMode || props.viewType === 'dayView' ? '8px' : '0')};
  height: 100%;
  ${(props) => props.viewType !== 'weekView' && 'overflow-x: hidden;'}
  ${(props) => props.viewType === 'monthView' && 'min-width: 0;'}
  ${(props) => props.viewType === 'weekView' && !props.noCalendarSelected && !props.isTvMode && props.programType !== 'free-form-program' && 'padding-top: 32px;'}
  ${(props) => props.viewType === 'weekView' && !props.noCalendarSelected && !props.isTvMode && 'padding-bottom: 48px;'}
  ${(props) => props.viewType === 'monthView' && 'background-color: #EEEEEE;'}
  ${(props) => props.isTvMode && 'padding-top: 40px;'}
`;

const SubHeaderTextWrapper = styled('div')`
  display: flex;
  width: 100%;
  min-height: ${(props) => (props.isTvMode ? '40px' : '32px')};
  max-height: ${(props) => (props.isTvMode ? '40px' : '32px')};
  background: ${(props) => (props.isTvMode ? (props.tvBackground || '#444444') : 'white')};
  align-items: center;
  padding-left: 10px;
  padding-right: ${(props) => (props.isTvMode ? '10px' : '0')};
  gap: 4px;
  ${(props) => props.isTvMode && 'border-bottom: 1px solid #EEE;'}
  ${(props) => props.isTvMode && 'justify-content: space-between;'}
  ${(props) => props.isTvMode && `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    width: 100vw;
    z-index: 95;
    box-sizing: border-box;
  `}
  ${(props) => props.viewType === 'weekView' && !props.isTvMode && 'position: fixed;'}
`;

const TvExitButton = styled('div')`
  height: 28px;
  width: 28px;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  :hover {
    background: ${(props) => (props.isPrimaryDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.08)')};
  }
`;

const WeekWrapper = styled('div')`
  display: flex;
  padding: 4px;
  border-radius: 4px;
  background: #F5F5F5;
  margin-left: 4px;
`;

const StatsBarContent = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: 16px;
`;

const StatsBarItem = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const CalendarContentContainer = styled('div')`
  display: flex;
  width: 100%;
  background-color: white;
  ${(props) => props.viewType === 'dayView' && 'overflow-x: hidden;'}
  ${(props) => props.viewType === 'monthView' && 'overflow-x: auto; min-width: 0;'}
  flex: 1;
  padding-left: ${(props) => {
    if (props.isTvMode) return props.viewType === 'weekView' ? '0' : '5px';
    return props.viewType === 'dayView' && '5px';
  }};
  padding-right: ${(props) => (props.isTvMode ? '0' : (props.viewType === 'dayView' && '5px'))};

  /* Hide the default scrollbar track */
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  /* Define scrollbar width */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  /* Style the scrollbar thumb */
  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 3px;
  }
  
  /* For Firefox */
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.2) transparent;

`;

const CalendarContent = () => {
  const containerRef = useRef(null);
  const lastHoveredDroppableRef = useRef(null);
  const dispatch = useDispatch();
  const theme = useTheme();
  const tvBackground = theme?.colorPrimary || '#444444';
  /** Pick contrasting foreground for the TV-mode subheader. Prefer the
   *  theme's pre-computed isPrimaryDarkTheme flag (set by skin admin), but
   *  fall back to a perceived-brightness calc on the hex when the flag is
   *  unset for the account — otherwise dark text lands on a dark primary. */
  const tvIsDark = (() => {
    if (typeof theme?.isPrimaryDarkTheme === 'boolean') return theme.isPrimaryDarkTheme;
    const hex = (tvBackground || '').replace('#', '');
    if (hex.length !== 6) return true;
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    if ([r, g, b].some(Number.isNaN)) return true;
    return ((r * 299) + (g * 587) + (b * 114)) / 1000 < 140;
  })();
  const tvForeground = tvIsDark ? 'white' : '#444444';
  const programType = useSelector((state) => state.calendar.data.programType);
  const selectedProgramDayNumber = useSelector(
    (state) => state.calendar.data.selectedProgramDayNumber,
  );
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const monthViewWindowStart = useSelector(
    (state) => state.calendar.data.monthViewWindowStart,
  );
  const date = new Date(selectedDate);
  const endpointFormattedDate = moment(date).format('YYYY-MM-DD');
  /** Program day totals are derived from the day's exercises (see utils/programDayTotals) — no
   * program endpoint returns volume, and the reducer doesn't recompute sets/reps on a cross-day
   * move. Calendar days read the stored, backend-computed values, which can be briefly missing
   * right after a client-side edit until the silent refetch lands — hence the || 0.
   * Selecting the program day object (rather than deriving inside the selector) keeps the selector
   * result reference-stable; a new object every call would re-render on every store change. */
  const programDay = useSelector(
    (state) => state.calendar.data.nonDateBasedProgramItems[selectedProgramDayNumber],
  );
  const calendarDaySetCount = useSelector(
    (state) => state.calendar.data.calendarItems[endpointFormattedDate]?.setCount || 0,
  );
  const calendarDayRepCount = useSelector(
    (state) => state.calendar.data.calendarItems[endpointFormattedDate]?.repCount || 0,
  );
  const calendarDayVolume = useSelector(
    (state) => state.calendar.data.calendarItems[endpointFormattedDate]?.totalVolumeLoad || 0,
  );
  const isFreeFormProgram = programType === 'free-form-program';
  const programTotals = getProgramDayTotals(programDay);
  const setCount = isFreeFormProgram ? programTotals.setCount : calendarDaySetCount;
  const repCount = isFreeFormProgram ? programTotals.repCount : calendarDayRepCount;
  const volume = isFreeFormProgram ? programTotals.totalVolumeLoad : calendarDayVolume;
  const selectedReduxCalendar = useSelector((state) => state.calendar.data.selectedReduxCalendar);
  const selectedReduxNonDateBasedProgram = useSelector(
    (state) => state.calendar.data.selectedReduxNonDateBasedProgram,
  );
  const viewType = useSelector((state) => state.calendar.data.viewType);
  const selectedReduxAthlete = useSelector((state) => state.calendar.data.selectedReduxAthlete);
  const isAnalyticsDisabled = useSelector(
    (state) => state.calendar.ui.isAnalyticsDisabled,
  );
  const isTvMode = useSelector((state) => state.calendar.ui.isTvMode);
  const isPrimaryAnimatedSideBarOpen = useSelector(
    (state) => state.calendar.data.isPrimaryAnimatedSideBarOpen,
  );
  const currentlyDisplayedWeekdays = useSelector(
    (state) => state.calendar.data.currentlyDisplayedWeekdays,
  );
  const selectedWeekViewDayToggle = useSelector(
    (state) => state.calendar.data.selectedWeekViewDayToggle,
  );

  const noCalendarSelected = (!hasKeys(selectedReduxCalendar)
  && !hasKeys(selectedReduxAthlete)
  && !hasKeys(selectedReduxNonDateBasedProgram));

  /** Two drag managers: the legacy one for day/week (and any source/dest
   * outside the month grid), and a separate one for month view. We route
   * each rbd event to the right manager based on the `month-day-` prefix
   * on the source or destination droppableId — so month-view drags never
   * enter the day/week orchestration code, and vice versa. */
  const dayWeekDrag = useDragManager();
  const monthDrag = useMonthViewDragManager();

  const isMonthEvent = (event) => {
    const sourceId = event?.source?.droppableId || '';
    const destId = event?.destination?.droppableId || '';
    const combineId = event?.combine?.droppableId || '';
    return sourceId.startsWith('month-day-')
      || destId.startsWith('month-day-')
      || combineId.startsWith('month-day-');
  };

  const handleDragStart = (start) => (
    isMonthEvent(start) ? monthDrag.handleDragStart(start) : dayWeekDrag.handleDragStart(start)
  );
  const handleDragEnd = (result) => (
    isMonthEvent(result) ? monthDrag.handleDragEnd(result) : dayWeekDrag.handleDragEnd(result)
  );
  /** onBeforeCapture only carries a draggableId, so we use viewType as
   * the discriminator: any drag starting while month view is active
   * belongs to the month manager. */
  const handleOnBeforeCapture = (before) => (
    viewType === 'monthView'
      ? monthDrag.handleOnBeforeCapture(before)
      : dayWeekDrag.handleOnBeforeCapture(before)
  );

  useEffect(() => {
    /** set info about top of scroll container to redux
       * we need this for tool tips
       */
    if (containerRef?.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      dispatch(setCalendarContainerScrollPositionTop(containerRect.top));
    }
  }, [dispatch]);

  // Clear active exercise when clicking outside any exercise item
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (!e.target.closest('[data-exercise-item]') && !e.target.closest('.superset-picker-menu') && !e.target.closest('.select-dropdown-exercise-picker__menu') && !e.target.closest('.progression-table-picker__menu')) {
        dispatch(setActiveExerciseId(null));
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [dispatch]);

  const getWeekForLastDayInDayToggleView = () => {
    const lastItem = currentlyDisplayedWeekdays[currentlyDisplayedWeekdays.length - 1];
    const lastItemWeek = moment(lastItem?.date).week();
    return lastItemWeek;
  };

  return (
    <DragDropContext
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onBeforeCapture={handleOnBeforeCapture}
      onDragUpdate={(update) => {
        const destinationDroppableId = update?.destination?.droppableId;

        if (destinationDroppableId && lastHoveredDroppableRef.current !== destinationDroppableId) {
          lastHoveredDroppableRef.current = destinationDroppableId;
          dispatch(setDroppableContainerCurrentlyHoveredByDraggedItem(destinationDroppableId));
        }
      }}
    >
      {!isTvMode && <IconSideBar />}
      <AnimatedSideBarPrimary />
      <CalendarContentAndStats
        viewType={viewType}
        isPrimaryAnimatedSideBarOpen={isPrimaryAnimatedSideBarOpen}
        isTvMode={isTvMode}
      >
        {(() => {
          const hasContext = hasKeys(selectedReduxCalendar)
            || hasKeys(selectedReduxAthlete)
            || hasKeys(selectedReduxNonDateBasedProgram);
          if (!hasContext) return null;
          if (isTvMode) {
            let tvHeaderText = '';
            if (programType === 'free-form-program') {
              tvHeaderText = selectedReduxNonDateBasedProgram?.programName || '';
            } else if (hasKeys(selectedReduxCalendar)) {
              tvHeaderText = selectedReduxCalendar?.name || '';
            } else if (hasKeys(selectedReduxAthlete)) {
              tvHeaderText = (selectedReduxAthlete?.first && selectedReduxAthlete?.last)
                ? `${selectedReduxAthlete.first} ${selectedReduxAthlete.last}`
                : '';
            }
            return (
              <SubHeaderTextWrapper
                viewType={viewType}
                isTvMode={isTvMode}
                tvBackground={tvBackground}
              >
                <Text fontSize='18px' fontWeight={700} color={tvForeground}>{tvHeaderText}</Text>
                <TvExitButton
                  data-tooltip-id='tv-mode-exit-tooltip'
                  aria-label='Exit TV Mode'
                  isPrimaryDark={tvIsDark}
                  onClick={() => dispatch(setIsTvMode(false))}
                >
                  <IcomoonReact
                    iconSet={iconSet}
                    size={14}
                    icon='remove'
                    color={tvForeground}
                  />
                </TvExitButton>
                <ReactTooltip
                  id='tv-mode-exit-tooltip'
                  place='left'
                  content='Exit TV Mode'
                  style={{
                    backgroundColor: 'rgba(68, 68, 68, 1)',
                    color: 'white',
                    fontFamily: 'Nunito Sans',
                    fontSize: '13px',
                  }}
                />
              </SubHeaderTextWrapper>
            );
          }
          if (programType === 'free-form-program') return null;
          if (viewType === 'monthView') {
            const {
              monthsLabel, yearLabel, firstWeek, lastWeek,
            } = getMonthViewWindowLabel(monthViewWindowStart);
            return (
              <SubHeaderTextWrapper viewType={viewType}>
                <Text fontSize='12px' color='#444444'>{monthsLabel}</Text>
                <Text fontSize='12px' color='#9E9E9E'>{yearLabel}</Text>
                <WeekWrapper>
                  <Text fontSize='12px' color='#9E9E9E'>
                    {`W${firstWeek} - W${lastWeek}`}
                  </Text>
                </WeekWrapper>
              </SubHeaderTextWrapper>
            );
          }
          return (
            <SubHeaderTextWrapper viewType={viewType}>
              <Text fontSize='12px' color='#444444'>{moment(selectedDate).format('MMM')}</Text>
              <Text fontSize='12px' color='#9E9E9E'>{moment(selectedDate).format('YYYY')}</Text>
              <WeekWrapper>
                <Text fontSize='12px' color='#9E9E9E'>
                  W
                  {moment(selectedDate).week()}
                  {viewType === 'weekView' && selectedWeekViewDayToggle !== 'Everyday' && ` - W${getWeekForLastDayInDayToggleView()}`}
                </Text>
              </WeekWrapper>
            </SubHeaderTextWrapper>
          );
        })()}
        <MonthYearWeekSubHeaderWrapper
          viewType={viewType}
          noCalendarSelected={noCalendarSelected}
          programType={programType}
          isTvMode={isTvMode}
        >
          {(hasKeys(selectedReduxNonDateBasedProgram) && programType === 'free-form-program' && viewType !== 'weekView') ? (
            <ProgramLibrarySubHeader />
          ) : null}
          {(((hasKeys(selectedReduxCalendar) && programType !== 'free-form-program') || (hasKeys(selectedReduxAthlete))) && viewType === 'dayView' && programType !== 'free-form-program') ? (
            <SubHeader />
          ) : null}
          <CalendarContentContainer
            ref={containerRef}
            viewType={viewType}
            isTvMode={isTvMode}
          >
            <CalendarPanel scrollContainerRef={containerRef} />
          </CalendarContentContainer>
          {((hasKeys(selectedReduxCalendar)
           || hasKeys(selectedReduxAthlete)
            || hasKeys(selectedReduxNonDateBasedProgram))) && !isAnalyticsDisabled && viewType !== 'weekView' && viewType !== 'monthView' ? (
              <StatsBar>
                <StatsBarContent>
                  <StatsBarItem>
                    <Text fontSize='13px' color='#BDBDBD'>Sets</Text>
                    <Text
                      marginLeft='5px'
                      marginTop='2px'
                      fontSize='15px'
                      color='#616161'
                      fontWeight='500'
                    >
                      <AnimatedNumber value={setCount} />
                    </Text>
                  </StatsBarItem>
                  <StatsBarItem>
                    <Text fontSize='13px' color='#BDBDBD'>Reps</Text>
                    <Text
                      marginLeft='5px'
                      marginTop='2px'
                      fontSize='15px'
                      color='#616161'
                      fontWeight='500'
                    >
                      <AnimatedNumber value={repCount} />
                    </Text>
                  </StatsBarItem>
                  <StatsBarItem>
                    <Text fontSize='13px' color='#BDBDBD'>Volume</Text>
                    <Text
                      marginLeft='5px'
                      marginTop='2px'
                      fontSize='15px'
                      color='#616161'
                      fontWeight='500'
                    >
                      <AnimatedNumber value={volume} />
                    </Text>
                  </StatsBarItem>
                </StatsBarContent>
              </StatsBar>
            ) : null}
        </MonthYearWeekSubHeaderWrapper>
        {hasKeys(selectedReduxNonDateBasedProgram) && programType === 'free-form-program' ? (
          <ProgramLibraryFooter />
        ) : null}
        {programType === 'calendar' && viewType === 'weekView' && (hasKeys(selectedReduxCalendar)
           || hasKeys(selectedReduxAthlete)) ? (
             <WeekViewDayToggleFooter />
          ) : null }
      </CalendarContentAndStats>
    </DragDropContext>
  );
};

export default CalendarContent;
